import Bestandsaufnahme from "@/models/ba/Bestandsaufnahme";
import { BestandsaufnahmeJson } from "@/models/ba/interfaces/IBestandsaufnahme";
import BestandsaufnahmeModel from "@/models/ba/models/bestandsaufnahme.model";
import { Response } from "@vuex-orm/plugin-axios";

/*
* This file contains helper functions for the data model API
*/

/** 
    * Update Bestandsaufnahme - Updates only the specified fields in the backend and returns only the values of all primitive fields as response via API.
    * If the updated field is a complex field like a relation this field is also being returned.
    * Uses VuexORMAxios but does not save the response to the store, please handle manually.
    * Pass optional JSON of existing survey to merge the new data with the old data in order to not lose values of complex fields like Fragenblocks
    * @param {number} baId The ID of the survey/BA
    * @param {number} projectId For access control
    * @param {any} data the fields to update
    * @param {BestandsaufnahmeJson} oldDataToMerge The old data of the survey to merge with the new data
    * @returns {Promise<BestandsaufnahmeJson>} The new survey as JSON
    * @throws Error If the API call fails or the response status is not 200
    */
export const updateBaFields = async (baId: number, projectId: number, data: any, oldDataToMerge?: BestandsaufnahmeJson) => {
    let response: Response;
    try {
        response = await BestandsaufnahmeModel.api().put(`/bestandsaufnahmes/updateFields/${baId}?projectId=${projectId}`, { data }, { save: false });
    } catch (error: any) {
        throw new Error("Unsuccessful request: " + error.response.statusText); // TODO: improve error message
    }

    if (response.response.status !== 200) {
        throw new Error("Unsuccessful request: " + response.response.statusText);
    }

    const responseSurvey = (response.getDataFromResponse() as any).data as BestandsaufnahmeJson;
    if (oldDataToMerge) {
        return Object.assign({}, oldDataToMerge, responseSurvey);
    } else {
        return responseSurvey;
    }
};

/**
 * Executes copyLeanJson first, then sends the updated data to the server. Extracts BestandsaufnaheJson from response and 
 * creates Bestandsaufnahme-object.
 * Used by RecursiveForm to update malus after saving hzba locally.
 * Used by Conclusion to update fazit.
 * Used by SignAndSubmitModal - click on Bestätigen
 * Used by BaSummary, click Abschließen, submitBestandsaufnahme / startSubmittingBestandsaufnahme
 * Used by useOnceBestandsaufnahmeSubpage / setCurrentHzba /onIonViewWillEnter (ba-details-page) to set areMinimumFragenblocksSet to true if not set (executed after creating new ba)
 * @param ba Bestandsaufnahme with updated data
 * @param projectId currentProject.value.id
 * @param deleteUnsaved true if hasUnsavedChanges should be set to false after sending updates to server
 * @param activateMinimum true if areMinimumFragenblocksSet should be set to true before sending updates to server
 * @returns Bestandsaufnahme object with updated data
 */
export const putFullBestandsaufnahme = async (ba: any, projectId: any, deleteUnsaved = false, activateMinimum = false) => {
    
    const copiedBa = await ba?.copyLeanJson({prepareForSync: true});
    if(activateMinimum){
        copiedBa["areMinimumFragenblocksSet"] = true;
    }

    const response = await sendBestandsaufnahmeUpdate(copiedBa, projectId);

    const updatedBaJsonRes = (response.getDataFromResponse() as any).data as BestandsaufnahmeJson;
    if(deleteUnsaved){
        updatedBaJsonRes.hasUnsavedChanges = false;
    }
    return new Bestandsaufnahme(updatedBaJsonRes, undefined);
}; 

/**
 * Sends the updated Bestandsaufnahme to the server with PUT request.
 * Used by handleOfflineSurveys and putFullBestandsaufnahme.
 * @param jsonBa Bestandsaufnahme with updated data
 * @param projectId currentProject.value.id
 * @returns response object
 */
export const sendBestandsaufnahmeUpdate = async (jsonBa: any, projectId: any) => {

    return await BestandsaufnahmeModel.api().put(`/bestandsaufnahmes/${jsonBa.id}?projectId=${projectId}`,
            { data: jsonBa },
            { save: false }
    );
}; 